import { supabase } from "@/integrations/supabase/client";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";
import { SalesReport, TransactionSuggestion } from "@/types/salesReport";
import { namedMarkAsReceivedFailure, settledRefusalOrNull } from "@/utils/errorUtils";

export class SalesReportApiClient {
  async getSalesReports(companyId: string): Promise<SalesReport[]> {
    try {
      const { data, error } = await supabase.functions.invoke('sales-report-api', {
        body: { action: 'getSalesReports', companyId }
      });

      if (error) {
        console.error('Error fetching sales reports:', error);
        throw await toEdgeFunctionFailure(error, 'Failed to fetch sales reports');
      }

      return data.salesReports || [];
    } catch (error) {
      console.error('Error calling sales-report API:', error);
      throw error;
    }
  }

  async getSalesReport(id: string): Promise<SalesReport | null> {
    try {
      const { data, error } = await supabase.functions.invoke('sales-report-api', {
        body: { action: 'getSalesReport', id }
      });

      if (error) {
        console.error(`Error fetching sales report ${id}:`, error);
        throw await toEdgeFunctionFailure(error, 'Failed to fetch sales report');
      }

      return data.salesReport || null;
    } catch (error) {
      console.error('Error calling sales-report API:', error);
      throw error;
    }
  }

  async markAsReceivedExternally(
    salesReportId: string,
    receivedDate: string,
    actualSekAmount?: number,
    transactionId?: string,
    bankAccountId?: string,
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('sales-report-api', {
        body: { action: 'markAsReceivedExternally', salesReportId, receivedDate, actualSekAmount, transactionId, bankAccountId }
      });

      if (error) {
        console.error(`Error marking sales report ${salesReportId} as received:`, error);
        // FIN-968: the chosen transaction already settles another document -- a named refusal.
        const { settled, body } = await settledRefusalOrNull(error);
        if (settled) throw settled;
        // FIN-1045: no fiscal year for the report date, or its bookkeeping could not be queued.
        const named = namedMarkAsReceivedFailure(body);
        if (named) throw named;
        throw await toEdgeFunctionFailure(error, 'Failed to mark sales report as received');
      }

      return data.success || false;
    } catch (error) {
      console.error('Error calling sales-report API:', error);
      throw error;
    }
  }

  async getTransactionSuggestions(salesReportId: string): Promise<TransactionSuggestion[]> {
    try {
      const { data, error } = await supabase.functions.invoke('sales-report-api', {
        body: { action: 'getTransactionSuggestions', salesReportId }
      });

      if (error) {
        console.error(`Error fetching transaction suggestions for sales report ${salesReportId}:`, error);
        throw await toEdgeFunctionFailure(error, 'Failed to fetch transaction suggestions');
      }

      return data.suggestions || [];
    } catch (error) {
      console.error('Error calling sales-report API:', error);
      throw error;
    }
  }

  /**
   * Precomputed candidate transactions (issue #457) — reads the async match_jobs pipeline's
   * already-computed auto_match_decisions suggestion instead of a live JIT scoring query. Sales
   * reports aren't yet part of that pipeline, so this always resolves to [] today — callers fall
   * back to getTransactionSuggestions (JIT) unchanged.
   */
  async getPrecomputedTransactionSuggestions(salesReportId: string): Promise<TransactionSuggestion[]> {
    try {
      const { data, error } = await supabase.functions.invoke('sales-report-api', {
        body: { action: 'getPrecomputedTransactionSuggestions', salesReportId }
      });

      if (error) {
        console.error(`Error fetching precomputed transaction suggestions for sales report ${salesReportId}:`, error);
        throw await toEdgeFunctionFailure(error, 'Failed to fetch precomputed transaction suggestions');
      }

      return data.suggestions || [];
    } catch (error) {
      console.error('Error calling sales-report API:', error);
      throw error;
    }
  }
}
